import { Theme } from '@material-ui/core/styles';
import { CSSProperties } from '@material-ui/core/styles/withStyles';

import { getStyles, getUseClasses, makeStyles, mockTheme } from '@core/theme/utils/with-styles';
import { coreButtonStyling } from '@shared/components/button-new/button.styles';
import { colors, colours } from '@shared/theme/colours';
import { ButtonSize, NewButtonColour, SelectColour } from '@shared/types/common/button';

// TODO: Change all hexcodes when palette is updated

type MenuItemStyling = {
  default: CSSProperties;
  hovered: CSSProperties;
  active: CSSProperties;
  disabled: CSSProperties;
};

const menuItemStyling: MenuItemStyling = {
  default: {
    backgroundColor: colors.white.lightest,
    color: colors.black.darker,

    '& $menuItemIcon svg': { color: colors.black.darker },
    '& $menuItemIcon svg path': { stroke: colors.black.darker },
    '& $menuItemIcon i': { color: colors.black.darker },
  },
  hovered: {
    backgroundColor: colors.kato.grey[100],
    color: colors.kato.navy[900],

    '& $menuItemIcon svg': { color: 'currentColor' },
    '& $menuItemIcon svg path': { stroke: 'currentColor' },
    '& $menuItemIcon i': { color: 'currentColor' },
  },
  active: {
    backgroundColor: colors.kato.grey[200],
    color: colors.kato.navy[900],

    '& $menuItemIcon svg': { color: 'currentColor' },
    '& $menuItemIcon svg path': { stroke: 'currentColor' },
    '& $menuItemIcon i': { color: 'currentColor' },
  },
  disabled: {
    backgroundColor: colors.white.lightest,
    color: '#9EA6B3',
    cursor: 'not-allowed',
    pointerEvents: 'none',

    '& $menuItemIcon svg': { color: 'currentColor' },
    '& $menuItemIcon svg path': { stroke: 'currentColor' },
    '& $menuItemIcon i': { color: 'currentColor' },
  },
};

export const getColourStyles = (
  theme: Theme,
  colour: Exclude<NewButtonColour, NewButtonColour.PrimarySpecial>,
  iconColour: string
) => {
  const coreStyles = coreButtonStyling[colour](theme);

  return {
    [`&:not($selectDisabled)$${colour} .MuiInputBase-root`]: {
      ...coreStyles.default,
      cursor: 'pointer',

      '& i': { color: iconColour },
      '& svg': { color: iconColour },
      '& svg path': { stroke: iconColour },
      '& svg.MuiSelect-icon': { color: iconColour },
      '& svg.MuiSelect-icon path': { stroke: iconColour },
    },
    [`&:not($selectDisabled)$${colour}:hover .MuiInputBase-root`]: {
      ...coreStyles.hovered,
      cursor: 'pointer',

      '& i': { color: iconColour },
      '& svg': { color: iconColour },
      '& svg path': { stroke: iconColour },
      '& svg.MuiSelect-icon': { color: iconColour },
      '& svg.MuiSelect-icon path': { stroke: iconColour },
    },
    [`&:not($selectDisabled)$${colour}$selectHovered .MuiInputBase-root`]: {
      ...coreStyles.hovered,
      cursor: 'pointer',

      '& i': { color: iconColour },
      '& svg': { color: iconColour },
      '& svg path': { stroke: iconColour },
      '& svg.MuiSelect-icon': { color: iconColour },
      '& svg.MuiSelect-icon path': { stroke: iconColour },
    },
    [`&:not($selectDisabled)$${colour}:active .MuiInputBase-root`]: {
      ...coreStyles.active,
      cursor: 'pointer',

      '& i': { color: iconColour },
      '& svg': { color: iconColour },
      '& svg path': { stroke: iconColour },
      '& svg.MuiSelect-icon': { color: iconColour },
      '& svg.MuiSelect-icon path': { stroke: iconColour },
    },
    [`&:not($selectDisabled)$${colour}$selectActive .MuiInputBase-root`]: {
      ...coreStyles.active,
      cursor: 'pointer',

      '& i': { color: iconColour },
      '& svg': { color: iconColour },
      '& svg path': { stroke: iconColour },
      '& svg.MuiSelect-icon': { color: iconColour },
      '& svg.MuiSelect-icon path': { stroke: iconColour },
    },
    [`&$selectDisabled$${colour} .MuiInputBase-root`]: {
      ...coreStyles.disabled,
      cursor: 'not-allowed',

      '& i': { color: 'currentColor' },
      '& svg': { color: 'currentColor' },
      '& svg path': { stroke: 'currentColor' },
      '& svg.MuiSelect-icon': { color: 'currentColor' },
      '& svg.MuiSelect-icon path': { stroke: 'currentColor' },
    },
  };
};

export const getSelectColourStyles = (theme: Theme, colour: SelectColour, iconColour: string) => {
  return getColourStyles(theme, colour, iconColour);
};

export const getCommonStyles = (theme: Theme) => {
  return getStyles({
    root: {
      '& fieldset': {
        border: 'none',
      },
      '& svg.MuiSelect-icon': {
        strokeWidth: 0, // Using only for down arrow icon to reduce icon stroke width
      },
      '& .MuiInputBase-root fieldset': {
        border: 'none',
      },

      '& .MuiInputBase-root': {
        border: '1px solid transparent',
        boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.07)',
        transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`, //Using transition object to be consistent with transition that used in material ui for button by default

        '&.Mui-error': {
          borderColor: '#EA3418 !important', //TODO replace after new palette creating
        },
      },

      // Colour styles
      ...getSelectColourStyles(theme, NewButtonColour.Ghost, '#7E8899'),
      ...getSelectColourStyles(theme, NewButtonColour.Outline, '#7E8899'),

      // Size
      ['&$' + ButtonSize.extraSmall + ' .MuiInputBase-root']: {
        height: 24,

        '&.MuiOutlinedInput-adornedStart': {
          paddingLeft: theme.spacing(1.5), // NOTE: Refer to size padding-left below
        },

        '& svg.MuiSelect-icon': {
          right: theme.spacing(0.5), // NOTE: Refer to size padding-left below - 4px
        },
      },
      ['&$' + ButtonSize.small + ' .MuiInputBase-root']: {
        height: 32,

        '&.MuiOutlinedInput-adornedStart': {
          paddingLeft: theme.spacing(3), // NOTE: Refer to size padding-left below
        },

        '& svg.MuiSelect-icon': {
          right: theme.spacing(1), // NOTE: Refer to size padding-left below - 8px
        },
      },
      ['&$' + ButtonSize.mediumLegacy + ' .MuiInputBase-root']: {
        height: 36,

        '&.MuiOutlinedInput-adornedStart': {
          paddingLeft: theme.spacing(3), // NOTE: Refer to size padding-left below
        },

        '& svg.MuiSelect-icon': {
          right: theme.spacing(1), // NOTE: Refer to size padding-left below - 8px
        },
      },
      ['&$' + ButtonSize.medium + ' .MuiInputBase-root']: {
        height: 40,

        '&.MuiOutlinedInput-adornedStart': {
          paddingLeft: theme.spacing(4), // NOTE: Refer to size padding-left below
        },

        '& svg.MuiSelect-icon': {
          right: theme.spacing(2), // NOTE: Refer to size padding-left below - 8px
        },
      },
      ['&$' + ButtonSize.large + ' .MuiInputBase-root']: {
        height: 48,

        '&.MuiOutlinedInput-adornedStart': {
          paddingLeft: theme.spacing(4), // NOTE: Refer to size padding-left below
        },

        '& svg.MuiSelect-icon': {
          right: theme.spacing(2), // NOTE: Refer to size padding-left below - 8px
        },
      },
    },
    rootFullWidth: {
      width: '100%',
    },

    select: {
      alignItems: 'center',
      backgroundColor: 'transparent',
      borderRadius: 6,
      color: 'inherit',
      cursor: 'inherit',
      display: 'flex',
      height: '100%',
      justifyContent: 'flex-start',

      '&.Mui-disabled': {
        color: 'inherit',
        cursor: 'inherit',
      },

      // Size
      ['&$' + ButtonSize.extraSmall]: {
        padding: theme.spacing(0, 8, 0, 1.5),

        '&.MuiOutlinedInput-inputAdornedStart': {
          paddingLeft: theme.spacing(0),
        },
      },
      ['&$' + ButtonSize.small]: {
        padding: theme.spacing(0, 9, 0, 3),

        '&.MuiOutlinedInput-inputAdornedStart': {
          paddingLeft: theme.spacing(0),
        },
      },
      ['&$' + ButtonSize.mediumLegacy]: {
        padding: theme.spacing(0, 10, 0, 3),

        '&.MuiOutlinedInput-inputAdornedStart': {
          paddingLeft: theme.spacing(0),
        },
      },
      ['&$' + ButtonSize.medium]: {
        padding: theme.spacing(0, 10, 0, 4),

        '&.MuiOutlinedInput-inputAdornedStart': {
          paddingLeft: theme.spacing(0),
        },
      },
      ['&$' + ButtonSize.large]: {
        padding: theme.spacing(0, 10, 0, 4),

        '&.MuiOutlinedInput-inputAdornedStart': {
          paddingLeft: theme.spacing(0),
        },
      },
    },

    menu: {
      border: '1px solid #EAECF0',
      borderRadius: 8,
      boxShadow: '0px 12px 24px -4px rgba(16, 24, 40, 0.10), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
      marginTop: theme.spacing(1),
      minWidth: 150,
      width: 'inherit',

      '& ul.MuiList-padding': {
        padding: 0,
      },
    },
    menuHeaderContent: {},
    menuItem: {
      alignItems: 'center',
      display: 'flex',
      fontSize: 14,
      padding: theme.spacing(2.5, 3.75),
      ...menuItemStyling.default,

      '& svg': {
        fontSize: 'inherit',
      },
      '& svg path': {},
      '& i': {
        fontSize: 'inherit',
      },

      '&.MuiListItem-root:hover': { ...menuItemStyling.hovered },
      '&.MuiListItem-root$menuItemHovered': { ...menuItemStyling.hovered },
      '&.MuiListItem-root:active': { ...menuItemStyling.active },
      '&.MuiListItem-root$menuItemActive': { ...menuItemStyling.active },
      '&.MuiListItem-root$menuItemDisabled': { ...menuItemStyling.disabled },

      '&.Mui-selected': {
        backgroundColor: colors.white.lightest,
      },
    },
    menuItemIcon: {
      alignItems: 'center',
      display: 'inline-flex',
      fontSize: 16,
      paddingRight: theme.spacing(2.5),
    },
    menuItemText: {},
    autoMenuHeight: {
      maxHeight: 'calc(100vh - 24px)',
      height: 'fit-content',
    },

    // Utility classes - DO NOT STYLE
    // Colours: Core
    [NewButtonColour.Ghost]: {},
    [NewButtonColour.Link]: {},
    [NewButtonColour.Outline]: {},
    // Colours: Additional used by dropdowns
    [NewButtonColour.Danger]: {},
    [NewButtonColour.PrimaryNavy]: {},
    [NewButtonColour.PrimaryLilac]: {},
    [NewButtonColour.PrimarySalmon]: {},
    [NewButtonColour.PrimarySpecial]: {
      // NOTE: This should not be styled, since this is a colour purely for app context
    },
    [NewButtonColour.PrimaryTeal]: {},
    [NewButtonColour.Subtle]: {},
    [NewButtonColour.Success]: {},
    [NewButtonColour.Transparent]: {},
    // Sizes
    [ButtonSize.extraSmall]: {},
    [ButtonSize.small]: {},
    [ButtonSize.medium]: {},
    [ButtonSize.mediumLegacy]: {},
    [ButtonSize.large]: {},
    // Icon sizes
    [ButtonSize.iconSmall]: {},
    [ButtonSize.iconLarge]: {},
    [ButtonSize.iconOnly]: {},
    // Other
    menuItemDisabled: {},
    selectDisabled: {},
    selectHovered: {},
    selectActive: {},
    selectError: {},
  });
};

const stylesFn = (theme: Theme) =>
  getStyles({
    ...getCommonStyles(theme),

    value: {
      color: colours.neutral[900],
      display: 'flex',
      fontSize: 14,
      fontWeight: 500,
      width: '100%',

      // Size
      ['&$' + ButtonSize.extraSmall]: {
        fontSize: 12,
      },
      ['&$' + ButtonSize.small]: {
        fontSize: 14,
      },
      ['&$' + ButtonSize.mediumLegacy]: {
        fontSize: 14,
      },
      ['&$' + ButtonSize.medium]: {
        fontSize: 14,
      },
      ['&$' + ButtonSize.large]: {
        fontSize: 16,
      },
    },
    valueDisabled: {},
    valueMultiple: {},
    valuePlaceholder: {
      color: 'currentColor',
      display: 'inline-flex',
      fontSize: 'inherit',
      opacity: 0.42,
    },

    valueIcon: {
      alignItems: 'center',
      display: 'inline-flex',
      fontSize: 16,
      paddingRight: theme.spacing(2),

      '& svg': {
        fontSize: 'inherit',
      },
      '& svg path': {},
      '& i': {
        fontSize: 'inherit',
      },
    },
    valueText: {
      color: 'currentColor',
      fontSize: 'inherit',
      lineHeight: 20,
      opacity: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '100%',
    },

    labelRoot: {
      // TODO: Make this consistent with TextField styling, when time permits. -Ben
      fontSize: 14,
      marginBottom: theme.spacing(2),
      color: '#1D2433', //TODO replace after new palette creating
    },
    labelError: {
      color: '#C52810 !important', //TODO replace after new palette creating
    },
    helperText: {
      paddingTop: theme.spacing(1),
    },
    // Utility classes - DO NOT STYLE
    menuItemActive: {},
    menuItemHovered: {},
  });
const stylesResult = stylesFn(mockTheme);

const makeClasses = makeStyles<keyof typeof stylesResult>('Select', stylesFn);

export const useSelectClasses = getUseClasses<keyof typeof stylesResult>(makeClasses);
